/**
 * SequenceTimelineBlock — ADR-055 canonical timeline.
 *
 * Renderização de ConversationSequence — conversas anteriores no mesmo
 * Bond+Person, cross-canal. Cada nó: posição + canal + data + outcome.
 *
 * Nó com `isCurrent: true` recebe destaque AI (--ai-bg/--ai-fg).
 */

import type { SequenceTimelineNode } from '../../hooks/use-panel-fixtures';

export function SequenceTimelineBlock({ nodes }: { nodes: SequenceTimelineNode[] }): JSX.Element {
  if (nodes.length === 0) {
    // Frente C (12/Ago/2026) — o texto antigo AFIRMAVA «Primeira conversa deste
    // cliente neste vínculo», mas nodes=[] também acontece com capability ausente,
    // durante o fetch e com bond sem Sequence ATIVA (janela 24h) — três casos em
    // que a afirmação podia ser mentira. O vazio só sustenta o que mede: ausência
    // de sequência ativa, nunca «primeira conversa» (regra afirmacao-de-ausencia).
    return (
      <p className="text-xs text-text-secondary opacity-70 italic">
        Nenhuma sequência de conversas ativa neste vínculo.
      </p>
    );
  }

  return (
    <ol className="sequence-timeline" data-testid="sequence-timeline">
      {nodes.map((n) => {
        // `channel`/`status`/`outcome` por nó são opcionais (o endpoint de
        // sequences ainda não os devolve por membro — item de backend).
        const headLabel =
          n.channel !== undefined
            ? channelLabelShort(n.channel)
            : n.isCurrent
              ? 'Conversa atual'
              : 'Conversa anterior';
        const meta = n.isCurrent ? 'em andamento' : (n.outcome ?? n.status ?? null);
        return (
          <li
            key={n.id}
            className="sequence-timeline__node"
            data-testid={`sequence-node-${n.position.toString()}`}
          >
            <span
              className={`sequence-timeline__dot ${
                n.isCurrent ? 'sequence-timeline__dot--current' : ''
              }`}
              aria-label={`Posição ${n.position.toString()} na sequência`}
            >
              {n.position}
            </span>
            <div className="sequence-timeline__body">
              <div className="sequence-timeline__head">
                <span>{headLabel}</span>
                <span style={{ fontSize: 10, color: 'var(--fg-3)' }}>{n.startedAt}</span>
              </div>
              {meta !== null && <div className="sequence-timeline__meta">{meta}</div>}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function channelLabelShort(c: string): string {
  if (c === 'whatsapp-cloud-api') return 'WhatsApp';
  if (c === 'sms') return 'SMS';
  if (c === 'voice') return 'Voz';
  if (c === 'email') return 'Email';
  return c;
}
